import {
    CameraServiceType,
    CaptureInfoFrequency,
    CaptureInfoPostType,
    CaptureInfoUploadMode,
    CellularInfoNetworkType,
    Status,
    WifiStatus,
} from '@EcamModel/model';
import { ConsolidateCameraMonitoring } from '@EcamModel/model/CameraOverview';
import { BaseSelect } from '@pages/cameras/list';
import { TableInfoContent } from '@pages/overview/hook/ReportsAnalyzingCriteriaAndPcnProvider';
import { SmartFilter } from '@pages/overview/smartFilterHighLight/configs';
import { DataTypes, SortCriterion, SortMode } from 'src/helpers/sortColumTable';
import { getColumnAlignment, getColumnMinWidth, getColumnWidth } from './columnConfigs';

export interface RowDataColumn {
    row: ConsolidateCameraMonitoring;
    columnId: keyof ConsolidateCameraMonitoring;
}

export interface PercentageRateProps {
    data: RowDataColumn;
    isCheckRate?: boolean;
}

export interface BackgroundColorAbsoluteProps {
    value: React.ReactNode;
    isCheckOnClick?: boolean;
    rowData: keyof ConsolidateCameraMonitoring;
    data: ConsolidateCameraMonitoring;
}

export enum CameraMonitoringMetric {
    PotentialContraventions = 'PotentialContraventions',
    AvgDailyPhotosTaken = 'AvgDailyPhotosTaken',
    PotentialRate = 'PotentialRate',
    ApprovalRate = 'ApprovalRate',
    CancellationRate = 'CancellationRate',
}

export const cameraStatusMapping = {
    [Status.Active]: 'Active',
    [Status.Inactive]: 'Inactive',
    [Status.NotAvailable]: 'Not available',
    [Status.Deleted]: 'Deleted',
};

export const netWorkingMapping = {
    [CellularInfoNetworkType.AUTO]: 'Auto',
    [CellularInfoNetworkType.ONLY_2G]: '2G only',
    [CellularInfoNetworkType.ONLY_3G]: '3G only',
    [CellularInfoNetworkType.ONLY_4G]: '4G only',
};

export const uploadModeMapping = {
    [CaptureInfoUploadMode.TIMING]: 'Timing',
    [CaptureInfoUploadMode.IMMEDIACY]: 'Immediacy',
};

export const wifiStatusMapping = {
    [WifiStatus.Connected]: 'Connected',
    [WifiStatus.DisConnected]: 'Disconnected',
};

export const postTypeMapping = {
    [CaptureInfoPostType.HTTP]: 'HTTP',
    [CaptureInfoPostType.MQTT]: 'MQTT',
};

export const cameraServiceMapping = {
    [CameraServiceType.ANPR]: 'ANPR',
    [CameraServiceType.Solar4G]: 'Solar',
    [CameraServiceType.PTZ]: 'View360',
    [CameraServiceType.SP111]: 'SP111',
};

export const frequencyTimeMapping = {
    [CaptureInfoFrequency.ONCE_A_DAY]: 'Once a Day',
    [CaptureInfoFrequency.TWICE_A_DAY]: 'Twice a Day',
};

export interface CameraOverviewPopup {
    cameraName: string;
    cameraId: number;
    locationName?: string;
}

export enum Mode {
    create,
    edit,
}

export enum TypeFilter {
    SMART,
    HIGHLIGHT,
    BATTERY,
    TASK,
}

export const statuses: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Active',
        Value: Status.Active,
    },
    {
        Id: 2,
        Name: 'Inactive',
        Value: Status.Inactive,
    },
    {
        Id: 3,
        Name: 'Not available',
        Value: Status.NotAvailable,
    },
    {
        Id: 4,
        Name: 'Deleted',
        Value: Status.Deleted,
    },
];

export const cameraVersions: BaseSelect[] = [
    {
        Id: 1,
        Name: '1',
        Value: 1,
    },
    {
        Id: 2,
        Name: '2',
        Value: 2,
    },
];

export const cameraDeleted: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Active',
        Value: 'Active',
    },
    {
        Id: 2,
        Name: 'Deleted',
        Value: 'Deleted',
    },
];

export const uploadMode: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Immediacy',
        Value: CaptureInfoUploadMode.IMMEDIACY,
    },
    {
        Id: 2,
        Name: 'Timing',
        Value: CaptureInfoUploadMode.TIMING,
    },
];

export const serviceType: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Solar',
        Value: CameraServiceType.Solar4G,
    },
    {
        Id: 2,
        Name: 'ANPR',
        Value: CameraServiceType.ANPR,
    }, 
    {
        Id: 3,
        Name: 'View360',
        Value: CameraServiceType.PTZ,
    },
    {
        Id: 4,
        Name: 'SP111',
        Value: CameraServiceType.SP111,
    },
    {
        Id: 5,
        Name: 'PMC8266',
        Value: CameraServiceType.PMC8266,
    },
];

export enum CameraType {
    CameraSolar,
    CameraView360,
    CameraSP111,
}

export const cameraTypesMapping = {
    [CameraType.CameraSolar]: 'CameraSolar',
    [CameraType.CameraView360]: 'CameraView360',
    [CameraType.CameraSP111]: 'CameraSP111',
};

export const monitoredMetrics: (keyof ConsolidateCameraMonitoring)[] = Object.values(CameraMonitoringMetric);

export const dataTypesCameraOverview: { [key in keyof ConsolidateCameraMonitoring]: DataTypes } = {
    // location
    LocationId: 'number',
    LocationName: 'string',

    // zone
    ZoneId: 'number',
    ZoneName: 'string',

    // camera
    CameraId: 'enum',
    CameraName: 'string',
    CameraStatus: 'enum',
    CameraDeployDate: 'date',
    CameraDeleted: 'date',
    CameraService: 'enum',
    CameraNoteCount: 'number',
    CameraTaskCount: 'number',
    CameraTaskOverDueCount: 'number',
    IsVerificationsEnabled: 'boolean',
    IP: 'string',
    LatestPhotoTime: 'date',
    TimeZone: 'string',

    // solar camera
    DevMac: 'string',
    CameraVersion: 'string',
    LastContact: 'date',
    LastWifiStatus: 'enum',

    // solar time info
    ServerTime: 'date',
    CameraTime: 'date',
    DifferentTime: 'number',
    DifferentMinute: 'number',
    SetTimeAutomatically: 'number',

    // solar capture info
    UploadMode: 'enum',
    CaptureTimeout: 'number',
    RadarSensitivity: 'number',
    LowSpeedLimit: 'number',
    Indicator: 'enum',
    PostType: 'enum',
    Frequency: 'enum',
    PostCaptureInterval: 'number',
    PostCaptureNum: 'number',

    // solar system info
    Firmware: 'string',

    // solar alarm ROI
    CaptureInterval: 'number',

    // solar cellular info
    NetworkType: 'enum',
    Apn: 'string',

    // solar cellular status
    Iccid: 'string',
    ConnectStatus: 'string',
    SimStatus: 'string',
    SignalStrength: 'number',

    // Avg Daily
    AvgDailyWorkedHours: 'number',
    AvgDailyPhotosTaken: 'number',

    // Total Taken Photos
    TotalPhotosTaken: 'number',

    // Avg TotalContraventions
    PotentialContraventions: 'number',
    InprogressContraventions: 'number',
    CancelledContraventions: 'number',
    ApprovedContraventions: 'number',

    // Rate TotalContraventions
    PotentialRate: 'number',
    ApprovalRate: 'number',
    CancellationRate: 'number',

    // solar Ping
    Pingable: 'boolean',

    // Upload mechanism
    ImagesLeft: 'number',

    // Purpose type
    PurposeType: 'enum',

    // Prediction date
    PredictedBatteryRunOutDate: 'date',

    // Battery
    AvgBattery: 'number',
    CurrentBattery: 'number',

    // Power supply
    CameraPowerSupply: 'enum',

    // Priority
    CameraPriority: 'number',

    CameraSDUsed: 'number',
    CameraSDFreeSpace: 'number',

    TakenPhotosToday: 'number',
    CameraSDCapacity: 'string',
    LastSwitchUploadModeAt: 'string',

    PTZLastContact: 'date',
};

export const getCellStyles = <T>(
    id: keyof T,
    dataTypes: { [key in keyof T]: DataTypes },
    stickyLeftFields: Array<keyof T>,
    alignmentFields: { [key in keyof T]?: React.CSSProperties['textAlign'] },
    stickyRightFields?: Array<keyof T>,
    isSelected?: boolean
) => {
    const isStickyLeftColumn = stickyLeftFields.includes(id);
    const isStickyRightColumn = stickyRightFields?.includes(id);

    const align = getColumnAlignment(id, alignmentFields as any) as 'left' | 'center';
    const minWidth = getColumnMinWidth(id, false);
    const stickyMinWidth = getColumnMinWidth(id, true);

    const leftPosition = isStickyLeftColumn
        ? stickyLeftFields
              .slice(0, stickyLeftFields.indexOf(id))
              .reduce((acc, key) => acc + getColumnWidth(key, true), isSelected ? 52 : 0)
        : undefined;

    const rightPosition = isStickyRightColumn
        ? stickyRightFields
              ?.slice(0, stickyRightFields.indexOf(id))
              .reduce((acc, key) => acc + getColumnWidth(key, true), 0)
        : undefined;

    return {
        align,
        sx: {
            padding: '4px 14px !important',
            backgroundColor: '#f0f0f0',
            minWidth: minWidth,
            ...(isStickyLeftColumn && {
                minWidth: stickyMinWidth,
                position: 'sticky',
                zIndex: stickyLeftFields.indexOf(id) === 0 ? 2 : 3,
                left: `${leftPosition}px`,
            }),
            ...(isStickyRightColumn && {
                minWidth: stickyMinWidth,
                position: 'sticky',
                zIndex: stickyRightFields?.indexOf(id) === 0 ? 2 : 3,
                right: `${rightPosition}px`,
            }),
        },
    };
};

export const getColumnStyles = <T>(
    columnId: keyof T,
    columnDataTypes: { [key in keyof T]: DataTypes },
    stickyLeftFields: Array<keyof T>,
    stickyRightFields?: Array<keyof T>,
    isSelected?: boolean
) => {
    const isLeftStickyColumn = stickyLeftFields.includes(columnId);
    const isStickyRightColumn = stickyRightFields?.includes(columnId);

    const minimumWidth = getColumnMinWidth(columnId, false);
    const stickyMinWidth = getColumnMinWidth(columnId, true);

    const leftPosition = isLeftStickyColumn
        ? stickyLeftFields
              .slice(0, stickyLeftFields.indexOf(columnId))
              .reduce((acc, key) => acc + getColumnWidth(key, true), isSelected ? 52 : 0)
        : undefined;

    return {
        padding: '4px 8px !important',
        backgroundColor: '#FAFAFA',
        minWidth: isLeftStickyColumn || isStickyRightColumn ? stickyMinWidth : minimumWidth,
        position: isLeftStickyColumn || isStickyRightColumn ? 'sticky' : 'unset',
        zIndex:
            isLeftStickyColumn || isStickyRightColumn
                ? isLeftStickyColumn
                    ? stickyLeftFields.indexOf(columnId) === 0
                        ? 2
                        : 3
                    : stickyRightFields?.indexOf(columnId) === 0
                      ? 2
                      : 3
                : 1,
        left: `${leftPosition}px`,
        right: isStickyRightColumn ? (stickyRightFields?.indexOf(columnId) === 0 ? 0 : '50px') : 'unset',
    };
};

const determineDataType = (value: any): DataTypes => {
    if (typeof value === 'string') {
        return 'string';
    }
    if (typeof value === 'number') {
        return 'number';
    }
    if (value instanceof Date) {
        return 'date';
    }
    if (Array.isArray(value)) {
        return 'array';
    }
    if (value && typeof value === 'object') {
        return 'object';
    }
    if (typeof value === 'boolean') {
        return 'boolean';
    }
    return 'any';
};

export const inferDataTypes = <T>(rows: T[]): { [K in keyof T]: DataTypes } => {
    if (!rows || rows.length === 0) {
        return {} as { [K in keyof T]: DataTypes };
    }

    const dataTypes: Partial<{ [K in keyof T]: DataTypes }> = {};
    const sampleRow = rows[0];

    for (const key in sampleRow) {
        const value = sampleRow[key];
        dataTypes[key] = determineDataType(value);
    }

    return dataTypes as { [K in keyof T]: DataTypes };
};

export const doesFieldExistInModel = <T>(headCellId: keyof T, model: Array<keyof T>): boolean => {
    return model.includes(headCellId);
};

export const handleRequestSort = <T>(
    property: keyof T,
    orderBy: SortCriterion<T>[],
    onSetOrderBy: (value: SortCriterion<T>[]) => void,
    isMultiSort: boolean = false
) => {
    if (!isMultiSort) {
        const isAsc = orderBy.length > 0 && orderBy[0].property === property && orderBy[0].order === SortMode.ASC;
        onSetOrderBy([{ property, order: isAsc ? SortMode.DESC : SortMode.ASC }]);
        return;
    }

    const existingIndex = orderBy.findIndex((item) => item.property === property);
    const newOrderBy = [...orderBy];

    if (existingIndex > -1) {
        const isAsc = newOrderBy[existingIndex].order === SortMode.ASC;
        isAsc ? (newOrderBy[existingIndex].order = SortMode.DESC) : newOrderBy.splice(existingIndex, 1);
        onSetOrderBy(newOrderBy);
    } else {
        newOrderBy.push({ property, order: SortMode.ASC });
        onSetOrderBy(newOrderBy);
    }
};

export const numberOfVersionsForOverview: string = '1.0.8';
export const localStorageCustomFieldsKey: string = 'eCamConfigCustomFields';
export const localStorageSmartFilterKey: string = 'smartFilters';
export const localStorageHighlightFilterKey: string = 'highlightFilters';
export const activeItemFilterKey: string = 'activeItemFilter';

export const getColumnsFieldCustom = (): TableInfoContent<keyof ConsolidateCameraMonitoring>[] => [
    {
        id: 'CameraDeployDate',
        label: 'Deployment\ndate',
        version: numberOfVersionsForOverview,
        type: 'Date',
        tooltipHover: 'Deployment date',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'TotalPhotosTaken',
        label: 'Total\nphotos',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Total photos',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'AvgDailyPhotosTaken',
        label: 'Avg\nphotos',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Avg. photos',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'PotentialContraventions',
        label: 'Potential',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Potential contraventions',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'InprogressContraventions',
        label: 'Verification',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Contraventions in verification',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'ApprovedContraventions',
        label: 'Approved',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Contraventions approved',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'CancelledContraventions',
        label: 'Cancelled',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Contraventions cancelled',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'PotentialRate',
        label: 'Potential\nrate',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Rate of potential contraventions',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'ApprovalRate',
        label: 'Approval\nrate',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Rate of approved contraventions',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'CancellationRate',
        label: 'Cancel\nrate',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Rate of cancel contraventions',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'Frequency',
        label: 'Photo\n upload',
        version: numberOfVersionsForOverview,
        type: 'List',
        tooltipHover: 'Photos upload time',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'PostCaptureInterval',
        label: 'Photo\ninterval',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Capture photos interval',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'PostCaptureNum',
        label: 'Photos\nper\nevent',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Number of capture photos per event',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'CameraVersion',
        label: 'Camera\nversion',
        version: numberOfVersionsForOverview,
        type: 'String',
        tooltipHover: 'Camera version',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'CameraStatus',
        label: 'Status',
        version: numberOfVersionsForOverview,
        type: 'List',
        tooltipHover: 'Operational status',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'AvgDailyWorkedHours',
        label: 'Uptime\n(h)',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Avg. uptime (h)',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'PurposeType',
        label: 'Purpose',
        version: numberOfVersionsForOverview,
        type: 'List',
        tooltipHover: 'Purpose type',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'CameraPowerSupply',
        label: 'Power\n supply',
        version: numberOfVersionsForOverview,
        type: 'List',
        tooltipHover: 'Power supply',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'ServerTime',
        label: 'Server\ntime',
        version: numberOfVersionsForOverview,
        type: 'Date',
        tooltipHover: 'Server time',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'CameraTime',
        label: 'Camera\ntime',
        version: numberOfVersionsForOverview,
        type: 'Date',
        tooltipHover: 'Camera time',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'DifferentMinute',
        label: 'Time\ndifference',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Time difference',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'PredictedBatteryRunOutDate',
        label: 'Battery\ndate',
        version: numberOfVersionsForOverview,
        type: 'Date',
        tooltipHover: 'Predicted battery depletion date',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'ZoneName',
        label: 'Zone',
        version: numberOfVersionsForOverview,
        type: 'String',
        tooltipHover: 'Zone',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'SetTimeAutomatically',
        label: 'Auto\ntime',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Auto time',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'CaptureInterval',
        label: 'Capture\ninterval',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Capture interval',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'Firmware',
        label: 'Firmware',
        version: numberOfVersionsForOverview,
        type: 'String',
        tooltipHover: 'Firmware',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'DevMac',
        label: 'MAC',
        version: numberOfVersionsForOverview,
        type: 'String',
        tooltipHover: 'Device MAC',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'NetworkType',
        label: '4G/3G/2G',
        version: numberOfVersionsForOverview,
        type: 'List',
        tooltipHover: 'Network mode 4G/3G/2G',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'CurrentBattery',
        label: 'Latest\nbattery',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Latest battery',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'AvgBattery',
        label: 'Avg.\nBattery',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Avg. Battery',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'LastContact',
        label: 'Last\ncontacted',
        version: numberOfVersionsForOverview,
        type: 'Date',
        tooltipHover: 'Last contact',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'Apn',
        label: 'APN',
        version: numberOfVersionsForOverview,
        type: 'String',
        tooltipHover: 'APN',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'Iccid',
        label: 'ICCID',
        version: numberOfVersionsForOverview,
        type: 'String',
        tooltipHover: 'Integrated Circuit Card ID',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'UploadMode',
        label: 'Upload\n mode',
        version: numberOfVersionsForOverview,
        type: 'List',
        tooltipHover: 'Upload mode',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'Pingable',
        label: 'Pingable',
        version: numberOfVersionsForOverview,
        type: 'List',
        tooltipHover: 'Latest ping',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'ConnectStatus',
        label: 'Connect\n status',
        version: numberOfVersionsForOverview,
        type: 'String',
        tooltipHover: 'Connect status',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'LastWifiStatus',
        label: 'Network\n status',
        version: numberOfVersionsForOverview,
        type: 'List',
        tooltipHover: 'Network status',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'SignalStrength',
        label: 'Signal',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Current signal strength',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'SimStatus',
        label: 'Sim\nstatus',
        version: numberOfVersionsForOverview,
        type: 'String',
        tooltipHover: 'SIM status',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'IsVerificationsEnabled',
        label: 'PCNs',
        version: numberOfVersionsForOverview,
        type: 'List',
        tooltipHover: 'Gen PCNs',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'PostType',
        label: 'Mechanism',
        version: numberOfVersionsForOverview,
        type: 'List',
        tooltipHover: 'Mechanism type',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'CameraSDUsed',
        label: 'Camera \nSD used',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'SD Used',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'CameraSDFreeSpace',
        label: 'Camera \nSD freespace',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'SD Freespace',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'ImagesLeft',
        label: 'Images\nleft',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Images left in SD',
        cameraTypes: [CameraType.CameraSolar],
    },
    {
        id: 'LatestPhotoTime',
        label: 'Latest\nphoto',
        version: numberOfVersionsForOverview,
        type: 'Date',
        tooltipHover: 'Time of the last photo taken',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'CameraTaskCount',
        label: 'Task',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Task',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'CameraTaskOverDueCount',
        label: 'Task\noverdue',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Task overdue',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
    {
        id: 'CameraNoteCount',
        label: 'Notes',
        version: numberOfVersionsForOverview,
        type: 'Number',
        tooltipHover: 'Notes',
        cameraTypes: [CameraType.CameraSolar, CameraType.CameraView360, CameraType.CameraSP111],
    },
];

export const customFieldIdsIncludes: (keyof ConsolidateCameraMonitoring)[] = ['CameraTaskCount'];
export const newTaskFilterData = getColumnsFieldCustom().filter((field) => customFieldIdsIncludes.includes(field.id));
export const newSmartFilter: SmartFilter[] = [
    {
        type: 'Number',
        options: [
            {
                field: ['CameraTaskCount'],
                condition: 'Greater Than Or Equal',
                value: 1,
            },
        ],
    },
];

export const batteryFieldIdsIncludes: (keyof ConsolidateCameraMonitoring)[] = [
    'AvgDailyWorkedHours',
    'CurrentBattery',
    'AvgBattery',
    'LastContact',
    'PotentialContraventions',
    'TotalPhotosTaken',
    'CameraPowerSupply',
    'PredictedBatteryRunOutDate',
    'IsVerificationsEnabled',
];
export const newBatteryFilterData = getColumnsFieldCustom().filter((field) =>
    batteryFieldIdsIncludes.includes(field.id)
);
